import type { ComponentProps, ReactNode } from 'react';
import { Button } from './Button';
import { cn } from '../../lib/utils';

/** The record's NAME as the control that opens it: the title of a card, a register row or a drawer entry,
 *  which is where a reader clicks first and where the keyboard's one tab stop for "open" sits.
 *
 *  It is a `Button` with `variant="ghost"` at no size of its own — a text-shaped control, not a 36px box —
 *  so it keeps the button's focus ring, disabled handling and keyboard behaviour while looking like the
 *  heading it names. Five cards had each spelled that out by hand (`h-auto max-w-full justify-start gap-0
 *  whitespace-normal rounded-sm px-0 py-0 … hover:bg-transparent hover:text-primary`): one long string,
 *  copied, which is how a card ends up opening with a slightly different hover than the card beside it.
 *
 *  The type role and the card's own group-hover stay with the caller, because they answer to the card's
 *  heading level and to which `group` its surface declares; everything that makes the control a control
 *  lives here.
 *
 *  CORE ONLY for now: it is not exposed through `window.ElowenUiRuntime`, so the registry's own copies stay
 *  until the next UI API cut (exposing it would move `PLUGIN_UI_API_VERSION` and force every plugin in the
 *  registry to ship a matching release). */
export function TitleLink({ children, className, ...props }: ComponentProps<typeof Button> & { children: ReactNode }) {
  return (
    <Button
      type="button"
      variant="ghost"
      // `h-auto` and the zero padding take the button's geometry out so the text sits on the heading's own
      // line; `whitespace-normal` and `max-w-full` let a long name wrap instead of running past the card.
      className={cn(
        'h-auto max-w-full justify-start gap-0 whitespace-normal rounded-sm px-0 py-0 text-left',
        'hover:bg-transparent hover:text-primary',
        className,
      )}
      {...props}
    >
      {children}
    </Button>
  );
}
